/**
=========================================================
* Elite 247 Admin Panel - Accept Invite
=========================================================
*/

import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import GoogleIcon from "components/GoogleIcon";
import MicrosoftIcon from "components/MicrosoftIcon";

import { useMaterialUIController, setLayout } from "context";
import { adminApi } from "services/api";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4901/api";

const REASON_MESSAGES = {
  expired: "This invite link has expired. Ask an admin to resend your invite.",
  already_accepted: "This invite has already been accepted. Try signing in instead.",
  not_found: "This invite is no longer valid.",
  invalid: "This invite link is invalid.",
};

function AcceptInvite() {
  const [, dispatch] = useMaterialUIController();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const urlError = searchParams.get("error");

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null); // { name, email }
  const [invalidReason, setInvalidReason] = useState(null);

  useEffect(() => {
    setLayout(dispatch, "page");
    return () => setLayout(dispatch, "dashboard");
  }, [dispatch]);

  useEffect(() => {
    if (!token) {
      setInvalidReason("invalid");
      setLoading(false);
      return;
    }
    adminApi
      .verifyInvite(token)
      .then((res) => {
        if (res.valid) {
          setInvite({ name: res.name, email: res.email });
        } else {
          setInvalidReason(res.reason || "invalid");
        }
      })
      .catch(() => setInvalidReason("invalid"))
      .finally(() => setLoading(false));
  }, [token]);

  // Either provider can accept an invite; the server checks the signed-in account's email
  // against the invited address and records whichever provider was used.
  const continueWith = (provider) => () => {
    window.location.href = `${API_URL}/auth/${provider}?invite_token=${encodeURIComponent(token)}`;
  };

  const providerButtonSx = {
    background: "#ffffff",
    color: "#5e5e5e !important",
    fontWeight: "bold",
    padding: "14px",
    borderRadius: "12px",
    textTransform: "none",
    border: "2px solid #8c8c8c",
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: { xs: "16px", sm: "24px" },
      }}
    >
      <Card
        sx={{
          p: { xs: 3, sm: 4 },
          maxWidth: 420,
          width: "100%",
          borderRadius: 3,
          textAlign: "center",
        }}
      >
        {loading ? (
          <MDBox py={4}>
            <CircularProgress color="info" />
          </MDBox>
        ) : invite ? (
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold" mb={1}>
              You&apos;re invited{invite.name ? `, ${invite.name}` : ""}
            </MDTypography>
            <MDTypography variant="body2" color="text" mb={3}>
              Sign in with the Microsoft or Google account for <strong>{invite.email}</strong> to
              accept your invite to the Elite 247 Admin panel.
            </MDTypography>

            {urlError && (
              <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
                {urlError}
              </Alert>
            )}

            <MDBox display="flex" flexDirection="column" gap={1.5}>
              <MDButton
                fullWidth
                onClick={continueWith("microsoft")}
                startIcon={<MicrosoftIcon />}
                sx={providerButtonSx}
              >
                Continue with Microsoft
              </MDButton>
              <MDButton
                fullWidth
                onClick={continueWith("google")}
                startIcon={<GoogleIcon />}
                sx={providerButtonSx}
              >
                Continue with Google
              </MDButton>
            </MDBox>
          </MDBox>
        ) : (
          <MDBox>
            <MDTypography variant="h5" fontWeight="bold" mb={2}>
              Invite unavailable
            </MDTypography>
            <MDTypography variant="body2" color="text" mb={3}>
              {REASON_MESSAGES[invalidReason] || REASON_MESSAGES.invalid}
            </MDTypography>
            <MDButton component={Link} to="/authentication/sign-in" color="info" variant="gradient">
              Go to Sign In
            </MDButton>
          </MDBox>
        )}
      </Card>
    </Box>
  );
}

export default AcceptInvite;
